import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import Header from '../../components/Header';
import CSVDrop from '../../components/CSVDrop';
import csv_template from '../../assets/simcase_csv_template.csv';

// Actions
import { gameActions } from '../../actions';

const Form = () => {
  // State
  const [onboarding, setOnboarding] = useState(false);
  const [item, setItem] = useState(null);

  // Hooks
  const dispatch = useDispatch();
  const game = useSelector((state) => state.game);
  const { t } = useTranslation();

  // Effects
  useEffect(() => {
    setOnboarding(true);
  }, []);

  useEffect(() => {
    if (game?.data) {
      setItem(game.data);
    }
  }, [game?.data]);

  // Methods
  const handleChange = (e) => {
    let newItem = { ...item };
    newItem[e.target.name] =
      e.target.name === 'players_text'
        ? e.target.value.replace(/\t/g, ', ')
        : e.target.value;
    newItem['changed'] = true;
    setItem(newItem);
  };

  const submit = () => {
    dispatch(gameActions.updateGame(item));
  };

  const launch = () => {
    dispatch(gameActions.launchGame(item));
  };

  const readCSV = (content) => {
    let newItem = { ...item };
    newItem.players_text = content;
    dispatch(gameActions.updateGame(newItem));
  };

  // Logic
  const okPPG = item?.registered?.length % item?.ppg === 0 ? true : false;
  const isSaving = item?.status === 'saving' ? true : false;
  const isLaunched = item?.status === 'launched' ? true : false;
  const isDisabled = !okPPG || isLaunched ? true : false;

  // Validation
  if (!item) return <div>{t('Loading...')}</div>;
  // if (item?.status === 'launched')
  //   return <Navigate to={'/games/' + item?._id + '/players'} />;

  // Render
  return (
    <div className=''>
      <Header />

      <h2 className='mt-3 sec-title fs-3'>
        {t('Simulation Setup')}{' '}
        <small
          style={{
            fontSize: '12px',
            padding: '5px 8px',
            verticalAlign: 'middle',
          }}
          className={
            item.status === 'saving'
              ? 'text-capitalize badge badge-pill text-bg-warning'
              : 'text-capitalize badge badge-pill text-bg-success'
          }>
          {item.status === 'saving' ? t('saving...') : t(item.status)}
        </small>
        <div className='float-end' style={{ lineHeight: 1 }}>
          <button
            className='btn btn-md btn-link text-dark me-5'
            onClick={() => setOnboarding(true)}>
            <small className='font-italic'>{t('Need help?')}</small>
          </button>
          <button
            className='btn btn-md btn-outline-primary'
            onClick={submit}
            disabled={isDisabled}>
            {t('Save')}
          </button>
          <button
            className='btn btn-md btn-outline-success ms-3'
            onClick={launch}
            disabled={isDisabled}>
            {t('Launch')}
          </button>
        </div>
      </h2>

      <div className='row mt-4'>
        <div className='col-6'>
          <div className='row'>
            <div className='col-8'>
              <div className='form-group'>
                <label>{t('Game Name')}</label>
                <div className='input-group input-group-lg mt-2'>
                  <input
                    name='name'
                    className='form-control'
                    placeholder={t('Enter game name')}
                    disabled={isDisabled || isSaving}
                    value={item.name || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='col-4'>
              <div className='form-group'>
                <label>{t('Access Type')}</label>
                <div className='input-group input-group-lg mt-2'>
                  <select
                    name='access_type'
                    className='form-control form-select'
                    value={item.access_type || ''}
                    onChange={handleChange}
                    disabled={isDisabled || isSaving}>
                    <option value='csv'>CSV</option>
                    {/* <option value="link">Link + Email</option> */}
                  </select>
                </div>
              </div>
            </div>

            <div className='col-8'>
              <div className='form-group mt-3'>
                <label>{t('AI Model (Conversation & Classification)')}</label>
                <div className='input-group input-group-lg mt-2'>
                  <select
                    name='ai_model'
                    className='form-control form-select'
                    value={item.ai_model || ''}
                    onChange={handleChange}
                    disabled={isDisabled || isSaving}>
                    <option value=''>{t('Select model')}</option>
                    <option value='claude-haiku-4-5-20251001'>
                      Claude 4.5 Haiku
                    </option>
                    <option value='claude-sonnet-4-6'>Claude 3.5 Sonnet</option>
                  </select>
                </div>
              </div>
            </div>

            {item.access_type === 'link' && (
              <div className='col-12 mt-4'>
                <div className='card'>
                  <div className='card-body bg-dark text-center pt-5 pb-5 pl-5 pr-5'>
                    <code style={{ fontSize: '2rem' }}>{item.short_url}</code>
                  </div>
                </div>
              </div>
            )}

            {item.access_type === 'csv' && (
              <div className='cok-12 mt-4'>
                <div className='card py-4 px-5'>
                  <ol className='ps-0 mb-0 pb-0'>
                    <li className='mb-3'>
                      <Trans
                        i18nKey='game_form_csv_instructions_1'
                        // Define the components list (for more complex structures)
                        components={[
                          // Index: 0
                          <a
                            className='fw-bold'
                            href={csv_template}
                            target='_blank'
                            rel='noreferrer'>
                            here
                          </a>,
                        ]}>
                        Structure your class list in a spreadsheet as follows
                        (click{' '}
                        <a
                          className='fw-bold'
                          href={csv_template}
                          target='_blank'
                          rel='noreferrer'>
                          here
                        </a>{' '}
                        to download a copy):
                      </Trans>
                      <table className='table table-sm table-bordered table-striped mt-3 font-monospace small'>
                        <tbody>
                          <tr>
                            <td>player1@email.com</td>
                            <td>Name</td>
                            <td>Surname</td>
                          </tr>
                          <tr>
                            <td>player2@email.com</td>
                            <td>Name</td>
                            <td>Surname</td>
                          </tr>
                          <tr>
                            <td>player3@email.com</td>
                            <td>Name</td>
                            <td>Surname</td>
                          </tr>
                          <tr>
                            <td>player4@email.com</td>
                            <td>Name</td>
                            <td>Surname</td>
                          </tr>
                        </tbody>
                      </table>
                    </li>
                    <li className='mb-3'>
                      {t(
                        "Copy and paste your data into the 'Registered Players' text area at right.",
                      )}
                    </li>
                    <li className='mb-0'>
                      {t(
                        "Click 'Save', verify the emails and names, then click 'Launch'.",
                      )}
                    </li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='col-6'>
          <div className='form-group'>
            <label>
              {t('Registered Players')}{' '}
              <span className='badge text-bg-warning badge-pill ms-1'>
                {item?.registered ? item?.registered?.length : 0}
              </span>
            </label>

            {item?.registered.length % item?.ppg !== 0 && (
              <small
                style={{
                  fontSize: '12px',
                  padding: '5px 8px',
                  verticalAlign: 'middle',
                }}
                className='badge badge-pill text-bg-danger float-end'>
                {t('game_form_ppg_error', { ppg: item.ppg })}
              </small>
            )}

            {item.access_type === 'csv' && (
              <CSVDrop onComplete={readCSV}>
                <div className='input-group input-group-lg mt-2 mb-2'>
                  <textarea
                    name='players_text'
                    className='form-control'
                    placeholder='email@example.com'
                    value={item.players_text || ''}
                    onChange={handleChange}
                    rows='15'
                    disabled={isDisabled || isSaving}></textarea>
                </div>
                <small>
                  <b>{t('Tip')}</b>:{' '}
                  {t('You can also drag and drop your csv file here.')}
                </small>
              </CSVDrop>
            )}

            {item.access_type === 'link' && (
              <ul class='list-group'>
                {item.registered.map((rp) => (
                  <li class='list-group-item'>{rp.email}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={onboarding}
        toggle={() => setOnboarding(!onboarding)}
        centered={true}
        size='lg'>
        <ModalHeader>{t('Onboarding')}</ModalHeader>
        <ModalBody className='text-center'>
          <iframe
            title={t("SimCase's Onboarding")}
            src='https://player.vimeo.com/video/439382598'
            width='640'
            height='360'
            allow='autoplay; fullscreen'
            allowFullScreen></iframe>
        </ModalBody>
        <ModalFooter>
          <button
            className='btn btn-outline-primary'
            onClick={() => setOnboarding(!onboarding)}>
            {t('Continue')}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export { Form as GameForm };
